import { useState, useMemo, useEffect } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { PageHeader } from "@shared/components/PageHeader";
import { useCategory, useSubCategory, useConsumerRelation } from "../../../../../hooks";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useTranslation } from "react-i18next";
interface ConsumerDetailProps {
  onDataChange?: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
  consumerData?: any;
  onFormReady?: (form: any) => void;
}

export function ConsumerDetail({ onDataChange, onValidationChange, consumerData, onFormReady }: ConsumerDetailProps) {
  const [subCategoryList, setSubCategoryList] = useState<string>("");
  const [isAddingCoApplicant, setIsAddingCoApplicant] = useState(false);
  const { t } = useTranslation();

  const { remoteUtilityId } = getLoginDataFromStorage();

  const { data: categoryData } = useCategory({ remote_utility_id: remoteUtilityId });
  const { data: subCategoryData } = useSubCategory({ 
    remote_utility_id: remoteUtilityId, 
    code_list: subCategoryList 
  });
  const { data: consumerRelationData } = useConsumerRelation();

  const relationshipOptions = useMemo(
    () =>
      consumerRelationData?.result?.map((item: any) => ({
        label: item.value,
        value: item.key,
        className: "",
      })) || [],
    [consumerRelationData]
  );

  const categoryOptions = useMemo(
    () =>
      categoryData?.result?.map((category) => ({
        value: category.code,
        label: category.name,
        className: "",
      })) || [],
    [categoryData]
  );

  const subCategoryOptions = useMemo(
    () =>
      subCategoryData?.result?.map((subCategory) => ({
        value: subCategory.code,
        label: subCategory.name,
        className: "",
      })) || [],
    [subCategoryData]
  );

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "firstName",
        label: t("First Name"),
        type: "text",
        required: true,
        maxLength: 50,
        placeholder: t("Enter first name"),
        helperText: t("Legal first name as per ID"),
        tooltipText: t("Enter your legal first name as shown on official identity document"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "lastName",
        label: t("Last Name"),
        type: "text",
        required: true,
        maxLength: 50,
        placeholder: t("Enter last name"),
        helperText: t("Legal last name as per ID"),
        tooltipText: t("Enter your legal last name as shown on official identity document"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "email",
        label: t("Email Address"),
        type: "email",
        required: true,
        placeholder: t("e.g. email@domain.com"),
        helperText: t("Bills and notifications will be sent to this email"),
        tooltipText: t("Your primary contact email address"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "phoneNumber",
        label: t("Phone Number"),
        type: "country-phone",
        required: true,
        placeholder: "+1 (555) 000-0000",
        helperText: t("Primary contact phone number"),
        tooltipText: t("Mobile or landline number with country code"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "accountCategory",
        label: t("Account Category"),
        type: "select",
        required: true,
        options: categoryOptions,
        placeholder: t("Select category"),
        helperText: t("Select your account category"),
        tooltipText: t("Classification of your utility account"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "accountSubCategory",
        label: t("Account Sub-Category"),
        type: "select",
        required: true,
        options: subCategoryOptions,
        disabled: !subCategoryList,
        placeholder: t("Select sub-category"),
        helperText: t("Select your account sub-category"),
        tooltipText: t("Specific sub-classification"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "ssnTaxId",
        label: t("SSN / Tax ID"),
        type: "text",
        required: false,
        placeholder: t("Enter SSN or Tax ID"),
        helperText: t("Optional government identifier"),
        tooltipText: t("Used for identity verification"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "dateOfBirth",
        label: t("Date of Birth"),
        type: "datepicker",
        required: false,
        placeholder: t("Select date of birth"),
        helperText: t("Optional date of birth"),
        tooltipText: t("Your date of birth"),
        showHelperTooltip: true,
        className: "col-span-4",
      },
      {
        name: "vipDesignation",
        label: t("VIP Designation"),
        type: "checkbox",
        required: false,
        helperText: t("Mark account as priority / VIP"),
        tooltipText: t("Check if this account requires VIP priority handling"),
        showHelperTooltip: true,
        className: "col-span-6",
      },
      {
        name: "addCoApplicant",
        label: t("Add Co-Applicant"),
        type: "checkbox",
        required: false,
        helperText: t("Add a secondary applicant to this account"),
        tooltipText: t("Optionally include a secondary contact or joint account holder"),
        showHelperTooltip: true,
        className: "col-span-6",
      },
       {
        name: "relationShip",
        label: t("Relationship to Primary"),
        type: "select",
        required: isAddingCoApplicant,
        options: relationshipOptions,
        helperText: t("Select relationship to primary applicant"),
        group: t("Co-Applicant Details"),
        groupColorNeeded: false,
        groupBorderNeeded: false,
        className: "col-span-6",
        showWhen: (formValues) => formValues.addCoApplicant === true,
      },
      {
        name: "coApplicantFirstName",
        label: t("First Name"),
        type: "text",
        required: isAddingCoApplicant,
        maxLength: 50,
        placeholder: t("Enter first name"),
        group: t("Co-Applicant Details"),
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: t("Co-applicant legal first name"),
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
      {
        name: "coApplicantLastName",
        label: t("Last Name"),
        type: "text",
        required: isAddingCoApplicant,
        maxLength: 50,
        placeholder: t("Enter last name"),
        group: t("Co-Applicant Details"),
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: t("Co-applicant legal last name"),
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
      {
        name: "coApplicantEmail",
        label: t("Email Address"),
        type: "email",
        required: isAddingCoApplicant,
        placeholder: t("e.g. email@domain.com"),
        group: t("Co-Applicant Details"),
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: t("Co-applicant email address"),
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
      {
        name: "coApplicantPhoneNumber",
        label: t("Phone Number"),
        type: "country-phone",
        required: isAddingCoApplicant,
        placeholder: "+1 (555) 000-0000",
        group: t("Co-Applicant Details"),
        groupColorNeeded: false,
        groupBorderNeeded: false,
        helperText: t("Co-applicant phone number"),
        showWhen: (formValues) => formValues.addCoApplicant === true,
        className: "col-span-6",
      },
     
    ],
    [subCategoryOptions, relationshipOptions, categoryOptions, subCategoryList, isAddingCoApplicant, t]
  );
const defaultFormValues = useMemo(() => {
  if (!consumerData?.result) return {};

  const data = consumerData.result;
  
  return {
    firstName: data.firstName || "",
    lastName: data.lastName || "",
    email: data.email || "",
    phoneNumber: data.contactNumber || "",
    accountCategory: data.category || "",
    accountSubCategory: data.subCategory || "",
    ssnTaxId: data.ssnEncrypted || "",
    vipDesignation: data.isVip || false,
    dateOfBirth: data.dateOfBirth || "",
    addCoApplicant: !!data.additionalData?.secondaryConsumer?.firstName,

    // Co applicant (if exists)
    coApplicantFirstName: data.additionalData?.secondaryConsumer?.firstName || "",
    coApplicantLastName: data.additionalData?.secondaryConsumer?.lastName || "",
    coApplicantEmail: data.additionalData?.secondaryConsumer?.email || "",
    coApplicantPhoneNumber: data.additionalData?.secondaryConsumer?.contactNumber || "",
    relationShip: data.additionalData?.secondaryConsumer?.secondaryRelationship || "",
  };
}, [consumerData]);;

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
  });

   useEffect(() => {
    if (onFormReady) {
      onFormReady(form);
    }
  }, [form, onFormReady]);

useEffect(() => {
  if (!consumerData?.result) return;

  // Step 1: set category first (which affects conditional fields)
  const data = consumerData.result;
  setSubCategoryList(data.category || "");
  setIsAddingCoApplicant(!!data.additionalData?.secondaryConsumer?.firstName);

  // Step 2: allow fields to mount based on showWhen
  setTimeout(() => {
    FormService.populateForm(form, defaultFormValues, formFields);
    const currentValues = form.getValues();
    onDataChange(buildPayload(currentValues));
  }, 0);
}, [consumerData, defaultFormValues, form]);

  const buildPayload = (formValues: any) => {

    const getCategoryLabel = () => {
    return categoryOptions.find(option => option.value === formValues.accountCategory)?.label || "";
  };

  const getSubCategoryLabel = () => {
    return subCategoryOptions.find(option => option.value === formValues.accountSubCategory)?.label || "";
  };
    const getRelationshipLabel = () => {
    return relationshipOptions.find(option => option.value == formValues.relationShip)?.label || "";
  };
  const relationshipLabel = getRelationshipLabel();
    const payload: any = {
      first_name: formValues.firstName || "",
      last_name: formValues.lastName || "",
      email: formValues.email || "",
      phone_number: formValues.phoneNumber || "",
      account_category: formValues.accountCategory || "",
       account_category_display: getCategoryLabel(),
      account_sub_category: formValues.accountSubCategory || "",
         account_sub_category_display: getSubCategoryLabel(),
      ssn: formValues.ssnTaxId || "",
      vip_designation: formValues.vipDesignation || false,
      date_of_birth: formValues.dateOfBirth || "",
      add_co_applicant: formValues.addCoApplicant || false,
    };

    if (formValues.addCoApplicant) {
      payload.co_applicant = {
        first_name: formValues.coApplicantFirstName || "",
        last_name: formValues.coApplicantLastName || "",
        email: formValues.coApplicantEmail || "",
        phone_number: formValues.coApplicantPhoneNumber || "",
        relationship: formValues.relationShip || "",
        relationship_display: relationshipLabel,
      };
    }

    return payload;
  };

const handleFieldChange = (fieldName: string, value: any) => {
  if (fieldName === "accountCategory") {
    setSubCategoryList(value);
    if (!value) {
      // Use shouldValidate: false and shouldTouch: false to prevent triggering onChange
      form.setValue("accountSubCategory", "", { 
        shouldValidate: false, 
        shouldDirty: false 
      });
    }
  }

  if (fieldName === "addCoApplicant") {
    setIsAddingCoApplicant(value);
  }

  // if (fieldName === "addCoApplicant" && !value) {
  //   // Clear co-applicant fields without triggering onChange
  //   form.setValue("coApplicantFirstName", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("coApplicantLastName", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("coApplicantEmail", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("coApplicantPhoneNumber", "", { shouldValidate: false, shouldDirty: false });
  //   form.setValue("relationShip", "", { shouldValidate: false, shouldDirty: false });
  // }
  

  if (onDataChange) {
    const currentValues = form.getValues();
    onDataChange(buildPayload(currentValues));
  }
};

  // Watch form validation state
 useEffect(() => {
  if (!onValidationChange) return;

  const subscription = form.watch(() => {
    const hasErrors = Object.keys(form.formState.errors)?.length > 0;
    onValidationChange(!hasErrors);
  });

  return () => subscription.unsubscribe();
}, [form, onValidationChange]);

  // Send initial data to parent - only once on mount
  // useEffect(() => {
  //   if (onDataChange ) {
  //     const currentValues = form.getValues();
  //     onDataChange(buildPayload(currentValues));
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []); // Empty dependency array - only run once on mount

  return (
    <div className="space-y-6">
      <div className="mb-4 flex items-center gap-4">
        <div className="flex flex-col">
          <PageHeader
            title={t("Consumer Details")}
            subtitle={t("Enter your personal and contact details for the new connection")}
          />
        </div>
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        onSubmit={() => {}}
        onFieldChange={handleFieldChange}
        gridColumns={12}
        className="grid-cols-12"
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: FIELD_CLASSES,
        }}
      />
    </div>
  );
}